import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, DownloadCloud, AlertCircle, CheckCircle, Search } from "lucide-react";
import { format } from "date-fns";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useAuth } from "@/contexts/AuthContext";
import { paymentService } from "@/services/paymentService";
import { Input } from "@/components/ui/input";

interface StudentPayment {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  studentPhone?: string;
  courseId: string;
  courseName: string;
  amount: number;
  paymentMethod: 'card' | 'cash';
  status: 'paid' | 'pending';
  createdAt: Date;
  updatedAt: Date;
  adminId?: string;
  adminName?: string;
  paymentDate?: Date;
}

const StudentPayments = () => {
  const { getFirestore } = useFirebase();
  const { user } = useAuth();
  const [payments, setPayments] = useState<StudentPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const db = getFirestore();
        if (!db) throw new Error("Firestore is not initialized");

        const paymentsRef = collection(db, "studentPayments");
        const paymentsQuery = query(paymentsRef, orderBy("createdAt", "desc"));
        const paymentsSnapshot = await getDocs(paymentsQuery);
        
        const paymentsData = paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate(),
          paymentDate: doc.data().paymentDate?.toDate()
        })) as StudentPayment[];

        setPayments(paymentsData);
      } catch (error) {
        console.error("Error fetching payments:", error);
        toast({
          title: "Error",
          description: "Failed to load payment data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [getFirestore, toast]);

  const handleMarkCashPaymentAsPaid = async (payment: StudentPayment) => {
    try {
      if (!user) {
        throw new Error("User not authenticated");
      }

      const adminName = `${user.firstName} ${user.lastName}`.trim() || "Admin";

      await paymentService.markCashPaymentAsPaid(
        payment.id,
        user.id,
        adminName
      );

      // Update local state
      setPayments(prevPayments =>
        prevPayments.map(p =>
          p.id === payment.id
            ? {
                ...p,
                status: 'paid',
                paymentDate: new Date(),
                adminId: user.id,
                adminName
              }
            : p
        )
      );

      toast({
        title: "Success",
        description: "Payment marked as paid successfully",
      });
    } catch (error) {
      console.error("Error marking payment as paid:", error);
      toast({
        title: "Error",
        description: "Failed to mark payment as paid",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Paid</Badge>;
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200">Pending</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const downloadPaymentsCSV = () => {
    const headers = ['ID', 'Student', 'Email', 'Phone', 'Course', 'Amount', 'Status', 'Payment Method', 'Created Date', 'Payment Date', 'Admin'];
    
    const csvRows = [
      headers.join(','),
      ...payments.map(payment => [
        payment.id,
        `"${payment.studentName}"`,
        payment.studentEmail,
        payment.studentPhone || '',
        `"${payment.courseName}"`,
        formatCurrency(payment.amount),
        payment.status,
        payment.paymentMethod,
        format(payment.createdAt, 'yyyy-MM-dd HH:mm:ss'),
        payment.paymentDate ? format(payment.paymentDate, 'yyyy-MM-dd HH:mm:ss') : '',
        payment.adminName || ''
      ].join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `student-payments-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "CSV Downloaded",
      description: "Student payment records have been downloaded as CSV",
    });
  };

  const filteredPayments = payments.filter(payment => {
    if (!payment) return false;
    const searchLower = searchQuery.toLowerCase();
    return (
      (payment.studentName?.toLowerCase() || '').includes(searchLower) ||
      (payment.studentEmail?.toLowerCase() || '').includes(searchLower) ||
      (payment.courseName?.toLowerCase() || '').includes(searchLower) ||
      (payment.id?.toLowerCase() || '').includes(searchLower)
    );
  });

  return (
    <DashboardLayout role="admin">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Student Payments</h1>
      </div>
      
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search payments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={downloadPaymentsCSV}
              disabled={payments.length === 0}
            >
              <DownloadCloud className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-lg font-medium text-gray-900">No Payments Found</p>
              <p className="text-gray-500 mt-1">
                {searchQuery ? "No payments match your search" : "There are no payment records in the system yet"}
              </p>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{payment.studentName}</div>
                          <div className="text-gray-500 text-xs">{payment.studentEmail}</div>
                          {payment.studentPhone && (
                            <div className="text-gray-500 text-xs">{payment.studentPhone}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{payment.courseName}</div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {payment.paymentMethod}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(payment.status)}
                          {payment.adminName && (
                            <div className="text-xs text-gray-500">
                              By: {payment.adminName}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500 whitespace-nowrap">
                        <div>{format(payment.createdAt, 'MMM d, yyyy')}</div>
                        <div className="text-xs">{format(payment.createdAt, 'h:mm a')}</div>
                      </TableCell>
                      <TableCell>
                        {payment.paymentMethod === 'cash' && payment.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkCashPaymentAsPaid(payment)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark as Paid
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default StudentPayments; 